import { useMemo } from 'react';
import type { BoxProps } from '@withjoy/joykit';
import type { HeaderBackgroundEffect, HeaderTitleStyle, NavigationStyle, WebsiteHeaderColors } from '@shared/types/WebsiteHeaderSettings.types';
import chroma from 'chroma-js';

/**
 * Helper function to convert HeaderBackgroundEffect to BoxProps styling.
 * Extracted from EventNameNavForStandaloneHeader.
 */
const getBackgroundEffectStyles = (effect: HeaderBackgroundEffect | undefined, backgroundColor?: string): BoxProps => {
  if (!effect) {
    return {};
  }

  const map: Record<HeaderBackgroundEffect, BoxProps> = {
    clear: {
      backgroundColor: 'transparent',
    },
    glass: {
      backgroundColor: 'rgba(255, 255, 255, 0.29)',
      borderRadius: '16px',
      boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
      backdropFilter: 'blur(5.6px)',
      border: '1px solid rgba(255, 255, 255, 0.26)',
    },
    transparent: {
      backgroundColor: chroma(backgroundColor || '#ffffff')
        .alpha(0.1)
        .hex(),
    },
    solid: {
      backgroundColor,
    },
  };

  return map[effect];
};

export interface UseHeaderStylesForGuestInput {
  headerTitleStyle?: HeaderTitleStyle;
  navigationStyle?: NavigationStyle;
  colorsData?: WebsiteHeaderColors;
}

export interface UseHeaderStylesForGuestResult {
  navTextColor: string;
  navEffectStyles: BoxProps;
  titleTextColor: string;
  titleEffectStyles: BoxProps;
  titleBackgroundColor?: string;
}

/**
 * Hook that computes header styles for guest site rendering.
 * Backfills effects from legacy colorsData when new style objects don't exist.
 *
 * Backfill logic:
 * - If colorsData.nameAndNavBackgroundColorEnabled: 'solid' effect with backgroundColor
 * - Otherwise: 'clear' effect
 * - Text color fallback chain: style.textColor → colorsData.nameAndNavFontColor → 'mono14'
 */
export const useHeaderStylesForGuest = ({ headerTitleStyle, navigationStyle, colorsData }: UseHeaderStylesForGuestInput): UseHeaderStylesForGuestResult => {
  const navTextColor = useMemo(() => {
    return navigationStyle?.navigationTextColor || colorsData?.nameAndNavFontColor || 'mono14';
  }, [navigationStyle, colorsData]);

  const navEffectStyles = useMemo<BoxProps>(() => {
    if (navigationStyle) {
      return getBackgroundEffectStyles(navigationStyle.backgroundEffect, navigationStyle.backgroundColor);
    }

    // Backfill from legacy colors
    if (colorsData?.nameAndNavBackgroundColorEnabled && colorsData.nameAndNavBackgroundColor) {
      return getBackgroundEffectStyles('solid', colorsData.nameAndNavBackgroundColor);
    }

    return getBackgroundEffectStyles('clear');
  }, [navigationStyle, colorsData]);

  const titleTextColor = useMemo(() => {
    return headerTitleStyle?.headerTextColor || colorsData?.nameAndNavFontColor || 'mono14';
  }, [headerTitleStyle, colorsData]);

  const titleEffectStyles = useMemo<BoxProps>(() => {
    if (headerTitleStyle) {
      return getBackgroundEffectStyles(headerTitleStyle.backgroundEffect, headerTitleStyle.backgroundColor);
    }

    // Backfill from legacy colors
    if (colorsData?.nameAndNavBackgroundColorEnabled && colorsData.nameAndNavBackgroundColor) {
      return getBackgroundEffectStyles('solid', colorsData.nameAndNavBackgroundColor);
    }

    return getBackgroundEffectStyles('clear');
  }, [headerTitleStyle, colorsData]);

  return {
    navTextColor,
    navEffectStyles,
    titleTextColor,
    titleEffectStyles,
  };
};
